import { useQuery } from "@tanstack/react-query";
import { Suspense, lazy, useMemo } from "react";
import { useIntl } from "react-intl";

import {
  DrawerContent,
  DrawerTopBar,
  SwipeableDrawer,
} from "@/components/Drawer";
import { useTravellersSelectionContext } from "@/features/travellers/contexts/TravellersSelectionContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import type {
  Traveller,
  TravellerAgeGroup,
} from "@/features/travellers/types/types";
import { getTravellerTypeAtTravelDate } from "@/features/travellers/utils/ageValidation";
import { travelerTypeToTypeCode } from "@/features/travellers/utils/transformTraveler";

import { TravellersList } from "./TravellersList";

const TravellerFormDrawer = lazy(() => import("./TravellerFormDrawer"));

export type TravellerSlot = { ageGroup: TravellerAgeGroup; index: number };

type TravellerSlotDrawerProps = {
  open: boolean;
  selectedSlot?: TravellerSlot;
  travelDate: Date;
  isSelectionDisabled?: (traveller: Traveller) => boolean;
  requiredAges?: number[];
  onTravellerSelected: (travellerId: string, slotIndex: number) => void;
  onClose: () => void;
};

export function TravellerSlotDrawer({
  open,
  // Workaround to always mount the below drawers. This does not affect any functionality
  selectedSlot = {
    ageGroup: "ADT",
    index: 0,
  },
  travelDate,
  isSelectionDisabled,
  requiredAges,
  onTravellerSelected,
  onClose,
}: TravellerSlotDrawerProps) {
  const intl = useIntl();
  const { data: travellers } = useQuery(travellersQueryOptions());
  const { travellerSlots, selectedTravellers } =
    useTravellersSelectionContext();

  const eligibleTravellers = useMemo(() => {
    if (!selectedSlot || !travellers) return [];
    return travellers.filter(
      (traveller) =>
        travelerTypeToTypeCode(
          getTravellerTypeAtTravelDate(traveller.birthDate, travelDate),
        ) === selectedSlot.ageGroup,
    );
  }, [travellers, selectedSlot, travelDate]);

  const slotName = intl.formatMessage(
    {
      defaultMessage:
        "{type, select, ADT {Adult} CHD {Child} INF {Infant} other {Traveller}} {number}",
      description: "Numbered traveller slot label (e.g. 'Adult 1', 'Child 2')",
    },
    {
      type: selectedSlot.ageGroup,
      number: selectedSlot.index + 1,
    },
  );
  const slotTitle = intl.formatMessage(
    {
      defaultMessage: "Add {name}",
      description: "Label to add a traveller to a booking slot",
    },
    {
      name: slotName,
    },
  );

  const hasEligibleTravellers = eligibleTravellers.length > 0;

  return (
    <>
      <Suspense fallback={null}>
        <TravellerFormDrawer
          open={!hasEligibleTravellers && open}
          traveller={null}
          travellerIdentifiableName={slotName}
          ageGroupConstraint={
            selectedSlot
              ? { ageGroup: selectedSlot.ageGroup, travelDate, requiredAges }
              : undefined
          }
          onSubmitSuccess={(traveller) => {
            if (!selectedSlot) return;

            onTravellerSelected(traveller.id, selectedSlot.index);
            onClose();
          }}
          onClose={onClose}
        />
      </Suspense>

      <SwipeableDrawer
        hasFullHeight
        open={hasEligibleTravellers && open}
        onClose={onClose}
        backgroundColor="background"
      >
        <DrawerTopBar onCloseClick={onClose} title={slotTitle} />
        <DrawerContent>
          <TravellersList
            travellers={eligibleTravellers}
            travelDate={travelDate}
            selectedTravellerId={
              travellerSlots[selectedSlot.ageGroup][selectedSlot.index]
            }
            travellerIdentifiableName={slotName}
            isSelectionDisabled={(traveller) => {
              const selectedTravellerId = selectedTravellers.find(
                (t) => t.id === traveller.id,
              )?.id;
              // Check if the traveller is already selected in the current slot
              if (selectedTravellerId) {
                return (
                  selectedTravellerId !==
                  travellerSlots[selectedSlot.ageGroup][selectedSlot.index]
                );
              }

              if (isSelectionDisabled) return isSelectionDisabled(traveller);

              return false;
            }}
            onTravellerFormSubmitSuccess={(traveller, action) => {
              if (action === "create") {
                onTravellerSelected(traveller.id, selectedSlot.index);
              }

              onClose();
            }}
            onTravellerSelected={(travellerId) => {
              onTravellerSelected(travellerId, selectedSlot.index);
              onClose();
            }}
          />
        </DrawerContent>
      </SwipeableDrawer>
    </>
  );
}
